import { ItemGrid } from "components/DesignSystem/Accordion/Accordion";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Loader from "components/DesignSystem/Loader/loader";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { JournalEntrySlider } from "components/JournalEntry/JournalEntrySlider";
import { ACCOUNTING_METHODS_OPTIONS } from "constants/accountMethod";
import { DISPLAY_DATE } from "constants/date";
import { JOURNAL_ENTRY_STATUS } from "constants/journalEntrySources";
import { LEDGER_TYPE_OPTIONS } from "constants/ledgerEntryTypes";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useGetRelatedLedgerEntryQuery } from "store/apis/generalLedger";

type LinkedJournalEntryCardProps = {
  ledgerEntryId: string;
  entityId: string;
};

export const LinkedJournalEntryCard = ({
  ledgerEntryId,
  entityId,
}: LinkedJournalEntryCardProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const jeSlider = useModal();

  const { data, isLoading, isSuccess } = useGetRelatedLedgerEntryQuery(
    { groupId, entityId, ledger_entry_id: ledgerEntryId },
    { skip: !groupId || !entityId }
  );

  const { ledger_entry } = data || {};
  const { entry_name, date, ledger_type, basis, status, amount } =
    ledger_entry || {};

  const isActive = status === JOURNAL_ENTRY_STATUS.ACTIVE;
  const ledgerTypeLabel = LEDGER_TYPE_OPTIONS.find(
    (o) => o.value === ledger_type
  )?.label;

  if (isLoading) {
    return (
      <div className="t-flex t-justify-center t-py-3">
        <Loader customType="secondary" />
      </div>
    );
  }

  if (!isSuccess || !ledger_entry) {
    return null;
  }

  return (
    <>
      <button
        className="all:unset t-w-full t-cursor-pointer"
        onClick={jeSlider.open}
      >
        <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-3 t-flex t-flex-col t-gap-3 hover:t-bg-surface-lighter-grey">
          <div className="t-flex t-justify-between t-items-center t-gap-2">
            <div className="t-text-body-sm t-text-text-60 t-truncate">
              {entry_name}
            </div>
            <div className="t-flex t-items-center t-gap-3 t-shrink-0">
              <Tag icon={false} rounded tagType={isActive ? "green" : "gray"}>
                {isActive ? "Active" : "Archived"}
              </Tag>
              <span className="t-text-text-30">
                <SmallArrowUpRight />
              </span>
            </div>
          </div>
          <ItemGrid>
            {Boolean(date) && (
              <InfoItem label="Entry for">
                {dayjs(date).format(DISPLAY_DATE)}
              </InfoItem>
            )}
            {ledgerTypeLabel && (
              <InfoItem label="Type">{ledgerTypeLabel}</InfoItem>
            )}
            {basis && (
              <InfoItem label="Basis">
                {ACCOUNTING_METHODS_OPTIONS[basis]}
              </InfoItem>
            )}
            <InfoItem label="Amount">
              <AmountSuperScript amount={Number(amount || 0)} />
            </InfoItem>
          </ItemGrid>
        </div>
      </button>

      <JournalEntrySlider
        isOpen={jeSlider.isOpen}
        onClose={jeSlider.close}
        ledgerEntryId={ledgerEntryId}
      />
    </>
  );
};
